import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Plus, Trash2, LogOut, Rss } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VedLogo from '../../components/VedLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_LABELS = {
  airbnb: 'Airbnb',
  makemytrip: 'MakeMyTrip',
  booking: 'Booking.com',
  other: 'Other',
};

const AdminICalSync = () => {
  const navigate = useNavigate();
  const [feeds, setFeeds] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null); // feed id being synced
  const [syncingAll, setSyncingAll] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFeed, setNewFeed] = useState({ room_id: '', platform: 'airbnb', ical_url: '' });
  const username = localStorage.getItem('admin_username');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };
      const [feedsRes, roomsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/ical-feeds`, { headers }),
        axios.get(`${BACKEND_URL}/api/rooms`),
      ]);
      setFeeds(feedsRes.data);
      setRooms(roomsRes.data);
      if (roomsRes.data.length > 0 && !newFeed.room_id) {
        setNewFeed(f => ({ ...f, room_id: roomsRes.data[0].id }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load iCal feeds');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeed = async () => {
    if (!newFeed.room_id || !newFeed.ical_url) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${BACKEND_URL}/api/admin/ical-feeds`, newFeed, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('iCal feed added successfully');
      setShowAddForm(false);
      setNewFeed(f => ({ ...f, ical_url: '' }));
      fetchData();
    } catch (error) {
      toast.error('Failed to add iCal feed');
    }
  };

  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm('Are you sure you want to delete this feed?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/ical-feeds/${feedId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Feed deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete feed');
    }
  };

  const handleSyncFeed = async (feedId) => {
    setSyncing(feedId);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/ical-feeds/${feedId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'Sync complete');
      fetchData();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/ical-feeds/sync-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const results = res.data.results || [];
      const successes = results.filter(r => r.status === 'success').length;
      const errors = results.filter(r => r.status === 'error').length;
      if (errors > 0) {
        toast.warning(`Sync complete: ${successes} succeeded, ${errors} failed`);
      } else {
        toast.success(`All ${successes} feeds synced successfully`);
      }
      fetchData();
    } catch (error) {
      toast.error('Sync all failed');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : roomId;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <VedLogo size="sm" showText={false} />
            <div>
              <h1 className="text-xl font-bold text-gray-800">iCal Sync</h1>
              <p className="text-sm text-gray-600">Welcome, {username}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back + title */}
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/admin/dashboard"
            className="flex items-center space-x-2 text-gray-600 hover:text-orange-600 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </Link>
          <div className="flex space-x-3">
            <button
              onClick={handleSyncAll}
              disabled={syncingAll || feeds.length === 0}
              className="flex items-center space-x-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
            >
              <RefreshCw size={16} className={syncingAll ? 'animate-spin' : ''} />
              <span>Sync All</span>
            </button>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 transition-colors"
            >
              <Plus size={16} />
              <span>Add Feed</span>
            </button>
          </div>
        </div>

        {/* Add Feed Form */}
        {showAddForm && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">Add iCal Feed</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
                <select
                  value={newFeed.room_id}
                  onChange={e => setNewFeed(f => ({ ...f, room_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>{room.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={newFeed.platform}
                  onChange={e => setNewFeed(f => ({ ...f, platform: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {Object.entries(PLATFORM_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">iCal URL</label>
                <input
                  type="url"
                  value={newFeed.ical_url}
                  onChange={e => setNewFeed(f => ({ ...f, ical_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-4">
              <button
                onClick={handleAddFeed}
                className="bg-orange-600 text-white px-5 py-2 rounded-lg hover:bg-orange-700 transition-colors"
              >
                Save Feed
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Feeds List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center space-x-2">
            <Rss className="text-orange-600" size={20} />
            <h2 className="text-lg font-bold text-gray-800">iCal Feeds</h2>
            <span className="ml-auto text-sm text-gray-500">{feeds.length} feed{feeds.length !== 1 ? 's' : ''}</span>
          </div>

          {feeds.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              <Rss className="mx-auto mb-3 text-gray-300" size={40} />
              <p>No iCal feeds added yet.</p>
              <p className="text-sm mt-1">Add a feed from Airbnb or MakeMyTrip to sync blocked dates.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {feeds.map(feed => (
                <li key={feed.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                        {PLATFORM_LABELS[feed.platform] || feed.platform}
                      </span>
                      <span className="font-medium text-gray-800">{getRoomName(feed.room_id)}</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 truncate max-w-xs md:max-w-md">{feed.ical_url}</p>
                    {feed.last_synced && (
                      <p className="mt-0.5 text-xs text-gray-400">
                        Last synced: {new Date(feed.last_synced).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4 shrink-0">
                    <button
                      onClick={() => handleSyncFeed(feed.id)}
                      disabled={syncing === feed.id}
                      className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg border border-blue-200 hover:border-blue-400 disabled:opacity-50 transition-colors"
                    >
                      <RefreshCw size={14} className={syncing === feed.id ? 'animate-spin' : ''} />
                      <span>Sync</span>
                    </button>
                    <button
                      onClick={() => handleDeleteFeed(feed.id)}
                      className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 px-3 py-1.5 rounded-lg border border-red-200 hover:border-red-400 transition-colors"
                    >
                      <Trash2 size={14} />
                      <span>Delete</span>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-4 text-sm text-gray-500 text-center">
          Syncing imports blocked dates from Airbnb / MakeMyTrip into the pricing calendar as unavailable.
        </p>
      </div>
    </div>
  );
};

export default AdminICalSync;
